import { selectQueue, projectState, SCHEDULER_VERSION, courseChoices } from "./domain.mjs";
import { projectJourney, trailStepCards } from "./journey.mjs";
import applicationChoices from "../data/application-choices.json" with { type: "json" };
import { read, update, pendingCount } from "./storage.mjs";
import { api, createSync } from "./sync.mjs";
import * as views from "./views.mjs";
const root = document.querySelector("#app");
let local = null,
  passages = [],
  cards = [],
  route = "today",
  status = "Opening…",
  notice = "",
  search = "",
  passage = null,
  session = null,
  trailPassage = null,
  trailRevealed = false,
  trailSheet = null,
  videoShown = null,
  celebration = null,
  welcome = false,
  students = [],
  teacherChallenges = {coverage:[], challenges:[]},
  challengeRound = null,
  loginMessage = "",
  loginUsername = "",
  listDestination = {route:"collection", search:""},
  loginOpen = false,
  emailSignIn = false,
  signInEmail = "",
  codeSent = false,
  // Which way of signing in is showing: "password", "email" (ask for a code) or "code" (enter it).
  loginMode = "password",
  accountEmailPending = "",
  linkToken = null,
  packStatus = "Not downloaded",
  videoStatus = "",
  storageStatus = "",
  downloading = false,
  updateAvailable = false;
const syncer = createSync((change) => {
  const prior = local?.snapshot.user.id;
  local = change.local;
  status = change.status;
  if (prior && prior !== local?.snapshot.user.id) {
    students = [];
    teacherChallenges = {coverage:[], challenges:[]};
    challengeRound = null;
    session = null;
    passage = null;
    route = "today";
    notice = "";
    welcome = false;
    loginOpen = false;
    accountEmailPending = "";
    loginUsername = "";
    writeLocation(true);
  }
  if (status.startsWith("Account changed.")) {
    loginOpen = true;
    loginMessage = status;
  }
  if (
    prior &&
    prior === local?.snapshot.user.id &&
    !status.startsWith("Account changed.")
  ) {
    renderBackground();
  } else render();
});
function model() {
  const settings = { ...local.snapshot.settings, ...local.settings };
  const events = [...local.snapshot.events, ...local.pending];
  const collectionIds = [
    ...new Set([...local.snapshot.collectionIds, ...local.additions]),
  ];
  const effective = {
    ...local,
    snapshot: { ...local.snapshot, collectionIds },
  };
  const challenges = local.cachedChallenges || [];
  const challenge = challenges.find(item => item.id === (challengeRound?.id || local.activeChallengeId) && item.status === "active");
  const passedKeys = (item) => new Set([...(item.ownAttempts || []), ...(local.pendingChallengeAttempts || []).filter(a => a.challengeId === item.id).map(a => ({...a, passed:a.skill === "location" ? a.rating >= 2 && !a.hinted : !!applicationChoices.find(q => q.passageId === a.passageId)?.defensibleIds.includes(a.choicePassageId) && a.knewWhy, creditedPassageId:a.skill === "application" ? a.choicePassageId : a.passageId}))].filter(a => a.passed && item.passageIds.includes(a.creditedPassageId)).map(a => `${a.creditedPassageId}:${a.skill}`));
  const challengeTask = challengeRound && challenge ? (challengeRound.task || null) : null;
  const queue = selectQueue(cards, events, collectionIds, settings),
    projection = projectState(cards, events),
    journey = projectJourney(passages, cards, events, new Date(), applicationChoices, challenges.flatMap(c => c.ownAttempts || []));
  const inCollection = new Set(collectionIds);
  return {
    local: effective,
    passages,
    cards,
    route,
    status,
    notice,
    search,
    passage,
    session,
    trailPassage,
    trailRevealed,
    trailSheet,
    videoShown,
    celebration,
    welcome,
    trailPlan: Object.fromEntries(journey.stops.map(stop => [stop.passageId, inCollection.has(stop.passageId) ? trailStepCards(stop, cards, projection, queue.newRemaining) : null])),
    students,
    teacherChallenges,
    challenges,
    challenge,
    challengeRound,
    challengeTask,
    challengePassed: item => passedKeys(item).size,
    settings,
    pending: pendingCount(local),
    packStatus,
    videoStatus,
    storageStatus,
    downloading,
    queue,
    projection,
    journey,
    applicationChoices: courseChoices(applicationChoices, passages),
    emailSignIn,
    accountEmailPending,
  };
}
function mainContent(m) {
  switch (route) {
    case "collection":
      return views.collectionView(m);
    case "browse":
      return views.collectionView(m, true);
    case "passage":
      return views.passageView(m);
    case "review":
      return views.celebrationView(m) + views.reviewView(m);
    case "trail":
      return views.trailExerciseView(m);
    case "progress":
      return views.progressView(m);
    case "settings":
      return views.settingsView(m);
    case "teacher":
      return views.teacherView(m);
    case "challenges":
      return views.challengesView(m);
    case "challenge":
      return views.challengeRoundView(m);
    default:
      return views.welcomeView(m) + views.todayView(m);
  }
}
let showingSignIn = false;
function render() {
  if (!local || loginOpen) {
    root.innerHTML = views.updateNotice(updateAvailable) + views.loginView(loginMessage, loginUsername, {emailSignIn, email:signInEmail, codeSent, linkPending:!!linkToken, mode:loginMode});
    showingSignIn = true;
    return;
  }
  const m = model();
  root.innerHTML = views.updateNotice(updateAvailable) + views.frame(m, mainContent(m));
  // The sign-in page can be scrolled down to its lower forms; the study screen starts at the top.
  if (showingSignIn) window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  showingSignIn = false;
  containModal();
}
function renderUpdateNotice() {
  const notice = root.querySelector("#app-update");
  if (notice) notice.hidden = !updateAvailable;
}
if ("serviceWorker" in navigator) {
  let controlled = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    // First installation claims the current page; it is not an upgrade.
    if (controlled) { updateAvailable = true; renderUpdateNotice(); }
    controlled = true;
  });
}
function hasUnfinishedWork() {
  return !!session?.card || [...root.querySelectorAll("form input")].some(input =>
    ["checkbox", "radio"].includes(input.type) ? input.checked !== input.defaultChecked : input.value !== input.defaultValue);
}
// Same-account background updates must not replace live forms, review controls
// or media. Their drafts/focus/playback belong to the learner, not the snapshot.
function renderBackground() {
  renderUpdateNotice();
  if (!local || loginOpen) return;
  const m = model(),
    connection = root.querySelector(".connection");
  if (!connection) return;
  connection.querySelector("#sync-status").textContent =
    status +
    (m.pending
      ? ` · ${m.pending} change${m.pending === 1 ? "" : "s"} waiting to sync`
      : "");
  // The status pill stays out of the way once everything is synced; it comes back when work is waiting.
  const calm = status === "All changes synced" && !m.pending;
  connection.classList.toggle("calm", calm);
  let reconnect = connection.querySelector('[data-action="reconnect"]');
  if (status.startsWith("Sign in again") && !reconnect) {
    reconnect = document.createElement("button");
    reconnect.type = "button";
    reconnect.className = "small";
    reconnect.dataset.action = "reconnect";
    reconnect.textContent = "Reconnect account";
    connection.append(reconnect);
  } else if (!status.startsWith("Sign in again")) reconnect?.remove();
  const noticeElement = root.querySelector("#action-notice");
  if (noticeElement) { noticeElement.textContent = notice; noticeElement.hidden = !notice; }
  const fresh = document.createElement("template");
  fresh.innerHTML = mainContent(m);
  const main = root.querySelector("#main");
  if (!main) return;
  // Entering or leaving full-screen study changes the frame itself, e.g. when a teacher closes a live round.
  if (main.classList.contains("studying") !== views.studying(m)) { render(); return; }
  const patch = (selector) => {
    const current = main.querySelector(selector),
      next = fresh.content.querySelector(selector);
    if (current && next) replaceMarkup(current, next.innerHTML);
  };
  for (const slot of ["pack-status", "storage-status", "video-status"]) patch(`[data-live="${slot}"]`);
  for (const action of ["pack", "video:" + passage?.id]) {
    const control = main.querySelector(`[data-action="${action}"]`);
    if (control) control.setAttribute("aria-disabled", String(downloading));
  }
  if (route === "settings") {
    for (const input of main.querySelectorAll("#settings-form input")) {
      const next = fresh.content.querySelector(`input[name="${input.name}"]`);
      if (next && input.value === input.defaultValue) {
        input.value = next.value;
        input.defaultValue = next.defaultValue;
      }
    }
    const settingsForm = main.querySelector("#settings-form");
    if (settingsForm) showPace(settingsForm);
  } else if (route === "teacher") {
    patch('[data-live="teacher-connection"]');
    const oldLabels = main.querySelectorAll(".release-list label span");
    fresh.content
      .querySelectorAll(".release-list label span")
      .forEach((label, index) => {
        if (oldLabels[index]) oldLabels[index].textContent = label.textContent;
      });
    const list = main.querySelector("#teacher-student-list");
    for (const next of fresh.content.querySelectorAll("[data-student]")) {
      const current = list.querySelector(
        `[data-student="${next.dataset.student}"]`,
      );
      if (!current) list.append(next.cloneNode(true));
      else
        for (const selector of [".student-progress", ".student-help"]) {
          replaceMarkup(
            current.querySelector(selector),
            next.querySelector(selector).innerHTML,
          );
        }
    }
    for (const control of main.querySelectorAll("button,input"))
      control.disabled = !navigator.onLine;
  } else if (route === "passage") {
    for (const slot of [
      "collection-status",
      "locating-status",
      "scripture-source",
      "help-status",
    ])
      patch(`[data-live="${slot}"]`);
  } else if (route === "review") {
    patch('[data-live="remaining-due"]');
  } else if (route === "browse" || route === "collection") {
    patch(".passage-grid");
  } else {
    const greeting = main.querySelector(".welcome"),
      nextGreeting = fresh.content.querySelector(".welcome");
    if (!greeting || greeting.outerHTML !== nextGreeting?.outerHTML || main.innerHTML === fresh.innerHTML) replaceMarkup(main, fresh.innerHTML);
    else {
      // Keep an unchanged greeting element, so its entrance plays once rather than again on every sync.
      const focused = greeting.contains(document.activeElement) ? document.activeElement : null,
        slot = document.createElement("i");
      slot.dataset.welcomeSlot = "";
      nextGreeting.replaceWith(slot);
      replaceMarkup(main, fresh.innerHTML);
      main.querySelector("[data-welcome-slot]").replaceWith(greeting);
      focused?.focus({ preventScroll: true });
    }
  }
  containModal();
}
// Replace only passive content. Preserve the logical keyboard destination if
// that refreshed content contains a focused link/button or section heading.
function replaceMarkup(target, html) {
  if (target.innerHTML === html) return;
  const active = document.activeElement;
  let focusSelector;
  if (target.contains(active)) {
    if (active.dataset.action)
      focusSelector = `[data-action="${CSS.escape(active.dataset.action)}"]`;
    else if (active.id) focusSelector = `#${CSS.escape(active.id)}`;
    else if (/^H[1-6]$/.test(active.tagName)) focusSelector = active.tagName;
  }
  target.innerHTML = html;
  if (focusSelector) {
    const destination = target.querySelector(focusSelector);
    if (destination) {
      if (/^H[1-6]$/.test(destination.tagName)) destination.tabIndex = -1;
      destination.focus({ preventScroll: true });
    }
  }
}

// Study screens open at the card rather than the page top, so on a phone the question and its first
// button fit on the screen with the navigation scrolled out of the way above.
function focusStudy(target = "") {
  const heading = document.querySelector("#main .station h2");
  if (heading) heading.tabIndex = -1;
  ((target && document.querySelector(`#main ${target}`)) || heading)?.focus({ preventScroll: true });
  document.querySelector("#main")?.scrollIntoView({ block: "start", behavior: "instant" });
}
function focusMain() {
  const heading = document.querySelector("#main h1, #main h2");
  if (heading) {
    heading.tabIndex = -1;
    heading.focus({ preventScroll: true });
  }
  window.scrollTo({ top: 0, left: 0, behavior: "instant" });
}
// Only public study navigation belongs in history. Session cards, answers,
// account identities and form values never enter a URL or a history state.
function writeLocation(replace = false) {
  const params = new URLSearchParams();
  if ((route === "browse" || route === "collection") && search) params.set("q", search);
  if (route === "passage") {
    params.set("from", listDestination.route);
    if (listDestination.search) params.set("q", listDestination.search);
  }
  const hash = `#${route}${route === "passage" && passage ? "/" + encodeURIComponent(passage.id) : ""}${params.size ? "?" + params : ""}`;
  if (location.hash !== hash) history[replace ? "replaceState" : "pushState"](null, "", hash);
}
function readLocation() {
  const [path, query = ""] = location.hash.slice(1).split("?");
  const [next, id] = path.split("/");
  const params = new URLSearchParams(query);
  route = ["today","collection","browse","passage","review","progress","challenges","challenge","settings","teacher"].includes(next) ? next : "today";
  if (route === "teacher" && local?.snapshot.user.role !== "teacher") route = "today";
  search = ["browse", "collection"].includes(route) ? params.get("q") || "" : "";
  if (route === "passage") {
    passage = passages.find(p => encodeURIComponent(p.id) === id);
    listDestination = {route:params.get("from") === "browse" ? "browse" : "collection", search:params.get("q") || ""};
    if (!passage) route = "collection";
  }
  if (route === "review" && !session) startSession();
}
const londonDay = () => new Intl.DateTimeFormat("en-CA", {timeZone:"Europe/London", year:"numeric", month:"2-digit", day:"2-digit"}).format(new Date());
// Greet a student with their progress when they sign in, and on their first visit of each day.
async function greet(always) {
  if (local?.snapshot.user.role !== "student" || route !== "today" || (!always && local.welcomedOn === londonDay())) return;
  welcome = true;
  try { await saveChange(current => ({...current, welcomedOn:londonDay()})); } catch {}
}
function startSession() {
  // No session size: every due review is offered, and the student stops with ×.
  session = {done:0, target:null, practice:0, met:[], intro:null, card:null, revealed:false, hinted:false, choice:null};
  nextCard();
}
async function navigate(next, {fromHistory = false, keepSearch = false} = {}) {
  const userId = local?.snapshot.user.id;
  route = next === "teacher" && local?.snapshot.user.role !== "teacher" ? "today" : next;
  notice = "";
  celebration = null;
  welcome = false;
  trailSheet = null;
  videoShown = null;
  if (!keepSearch) search = "";
  if (!fromHistory) writeLocation();
  if (route === "teacher" && navigator.onLine) await teacherRefresh();
  if (route === "challenge" && !challengeRound && local.activeChallengeId) beginChallenge(local.activeChallengeId);
  if (route === "settings" || route === "passage") await downloadStatus();
  if (!local || local.snapshot.user.id !== userId) return;
  render();
  if (route === "challenge" && challengeRound?.task) focusStudy('[data-action="challenge-reveal"]');
  else focusMain();
}
window.addEventListener("popstate", async () => {
  // A sign-in link opened in a tab that already shows the app changes only the fragment.
  if (location.hash.startsWith("#verify")) { takeLinkToken(); loginMessage = ""; render(); return; }
  if (!local || loginOpen) { render(); return; }
  readLocation();
  await navigate(route, {fromHistory:true, keepSearch:true});
});
async function teacherRefresh() {
  if (!navigator.onLine) return;
  const userId = local?.snapshot.user.id;
  try {
    const result = await api("/api/teacher/students");
    const challengeResult = await api("/api/teacher/challenges");
    if (
      local?.snapshot.user.id === userId &&
      local.snapshot.user.role === "teacher"
    )
      { students = result.students; teacherChallenges = challengeResult; }
  } catch (e) {
    if (local?.snapshot.user.id === userId) notice = e.message;
  }
}
function chooseChallengeTask() {
  if (!challengeRound) return;
  const c = (local.cachedChallenges || []).find(item => item.id === challengeRound.id && item.status === "active");
  if (!c) {challengeRound.task = null; return;}
  const passed = new Set([...(c.ownAttempts || []), ...(local.pendingChallengeAttempts || [])].filter(a => a.challengeId === c.id && (a.passed || (a.skill === "location" ? a.rating >= 2 && !a.hinted : !!applicationChoices.find(q => q.passageId === a.passageId)?.defensibleIds.includes(a.choicePassageId) && a.knewWhy))).map(a => `${Object.hasOwn(a,"creditedPassageId") ? a.creditedPassageId : a.skill === "application" ? a.choicePassageId : a.passageId}:${a.skill}`));
  const allAttempts = [...(c.ownAttempts || []), ...(local.pendingChallengeAttempts || []).filter(a => a.challengeId === c.id)];
  const today = new Intl.DateTimeFormat("en-CA", {timeZone:"Europe/London",year:"numeric",month:"2-digit",day:"2-digit"}).format(new Date());
  const day = at => new Intl.DateTimeFormat("en-CA", {timeZone:"Europe/London",year:"numeric",month:"2-digit",day:"2-digit"}).format(new Date(at));
  challengeRound.task = c.passageIds.flatMap(passageId => c.skills.map(skill => ({passageId,skill}))).find(task => !passed.has(`${task.passageId}:${task.skill}`) && !challengeRound.tried.includes(`${task.passageId}:${task.skill}`) && !allAttempts.some(a => a.passageId === task.passageId && a.skill === task.skill && day(a.at) === today)) || null;
  if (challengeRound.task) {
    const prior = allAttempts.filter(a => a.passageId === challengeRound.task.passageId && a.skill === challengeRound.task.skill);
    challengeRound.promptVariant = prior.length % 2 ? "retry" : "initial";
  }
  challengeRound.revealed = false;
  challengeRound.choice = null;
  challengeRound.feedback = null;
}
function beginChallenge(id) {
  challengeRound = {id, task:null, tried:[], revealed:false, choice:null, feedback:null};
  chooseChallengeTask();
}
async function recordChallengeAttempt(values) {
  const c = challengeRound, task = c?.task;
  if (!task) return;
  const attempt = {id:crypto.randomUUID(),challengeId:c.id,passageId:task.passageId,skill:task.skill,at:new Date().toISOString(),...values};
  await saveChange(current => ({...current,pendingChallengeAttempts:[...(current.pendingChallengeAttempts || []),attempt]}));
  c.tried.push(`${task.passageId}:${task.skill}`);
  const selection = applicationChoices.find(item => item.passageId === task.passageId);
  const passed = task.skill === "location" ? values.rating >= 2 && !values.hinted : !!selection?.defensibleIds.includes(values.choicePassageId) && values.knewWhy;
  const inCorpus = task.skill === "location" || (local.cachedChallenges || []).find(item => item.id === c.id)?.passageIds.includes(values.choicePassageId);
  c.feedback = {passed:passed && inCorpus,message: passed ? (inCorpus ? "You knew where this passage fits. Your check will count when synced." : "That is a useful passage. It sits outside this round's passage list, so this is personal practice.") : "Good practice. Return after another step and try a fresh attempt."};
  render();
  root.querySelector(".answer-panel")?.focus();
}
async function saveChange(change) {
  const id = local.snapshot.user.id;
  const changed = await update((current) => {
    if (
      localStorage.getItem("dm-signed-out") ||
      current?.snapshot.user.id !== id
    )
      throw new Error("Account changed. Please reopen your study.");
    return change(current);
  });
  if (local?.snapshot.user.id !== id || localStorage.getItem("dm-signed-out"))
    throw new Error("Account changed. Please sign in again.");
  local = changed;
  renderBackground();
  void syncer.sync();
}
function nextCard() {
  // A Caravan Trail session works through its own short list instead of the daily queue.
  const m = model();
  // A trail stop's own cards come first; after them the session carries on with due reviews only,
  // so a "Not yet" there still comes back.
  if (session.queue && !session.queue.length) Object.assign(session, {queue:null, target:null, reviewsOnly:true});
  const known = (c) => m.projection.byCard[c.id].introduced;
  // Due practice always comes; only one new explanation or application prompt joins a session.
  session.card = session.queue
    ? session.queue.shift()
    : m.queue.cards.find((c) => session.reviewsOnly ? known(c) : c.mode !== "practice" || known(c) || session.practice === 0) || null;
  session.revealed = false;
  session.hinted = false;
  session.choice = null;
  // Teach before testing: a passage the student has never studied is shown before its first question.
  const id = session.card?.passageId;
  const met = !id || session.met.includes(id) || Object.values(m.projection.byCard).some(s => s.introduced && s.card.passageId === id && s.card.kind !== "help");
  session.intro = met ? null : id;
}
const studyFocus = () => session?.intro ? '[data-action="intro-ready"]' : session?.card?.kind === "apply" ? '[data-action="apply-options"]' : '[data-action="reveal"]';
async function completeDay() {
  if (!session?.done || model().journey.todayComplete) return;
  const today = new Intl.DateTimeFormat("en-CA", {timeZone:"Europe/London", year:"numeric", month:"2-digit", day:"2-digit"}).format(new Date());
  const cardId = `daily:${today}:v1`;
  if ([...local.snapshot.events, ...local.pending].some(event => event.cardId === cardId)) return;
  await saveChange(current => ({...current, pending:[...current.pending, {id:crypto.randomUUID(), cardId, at:new Date().toISOString(), baseEventId:null, mode:"daily", result:"done"}]}));
}
async function record(card, values) {
  const m = model();
  const event = {
    id: crypto.randomUUID(),
    cardId: card.id,
    at: new Date().toISOString(),
    baseEventId: m.projection.byCard[card.id].lastEventId,
    ...values,
  };
  await saveChange((current) => ({
    ...current,
    pending: [...current.pending, event],
  }));
}
async function rate(values) {
  if (!session?.card || !session.revealed) return;
  const card = session.card;
  session.revealed = false;
  celebration = null;
  const before = model().journey, introduced = model().projection.byCard[card.id].introduced;
  await record(card, values);
  celebration = newBadges(before, model().journey);
  session.done++;
  if (card.mode === "practice" && !introduced) session.practice++;
  nextCard();
  if (!session.card) await completeDay();
  render();
  focusStudy(views.bigCelebration(celebration) ? '[data-action="celebration-close"]' : studyFocus());
}
// Compare the trail before and after one answer, so a newly won badge can be celebrated.
function newBadges(before, after) {
  const earned = after.stops.flatMap((stop, index) => {
    const prior = before.stops[index];
    return ["location", "application"].filter(skill => stop[skill] && !prior[skill]).map(skill => ({passageId:stop.passageId, skill, complete:stop.complete}));
  });
  return earned.length ? {earned, points:after.points, completedStops:after.completedStops, summit:after.summitReached && !before.summitReached} : null;
}
async function cacheFile(cache, url) {
  const response = await fetch(url, { cache: "reload" });
  if (response.status !== 200)
    throw new Error(`Could not download ${url.split("/").pop()}`);
  const blob = await response.blob();
  const length = Number(response.headers.get("content-length"));
  if (!blob.size || (length && blob.size !== length))
    throw new Error("Incomplete download");
  await cache.put(
    url,
    new Response(blob, { status: 200, headers: response.headers }),
  );
}
function packMediaUrls(list = passages) {
  return [...new Set(list.flatMap((p) => (p.imageUrl ? [p.imageUrl] : [])))].sort();
}
function packVersion(list = passages) {
  return packMediaUrls(list).join("\n");
}
function preparedMedia(pathname) {
  return /^\/media\/([a-z0-9-]+)\/(scene|video|captions)\.(?:[0-9a-f]{12}\.)?(png|mp4|vtt)$/.exec(pathname);
}
// Remove earlier versions of the given media kinds once their replacements are saved, keeping
// anything the given curriculum still uses. Videos are only replaced when a new video is downloaded.
async function collectSupersededMedia(cache, list, kinds, passageId) {
  const current = new Set(list.flatMap((p) => [p.imageUrl, p.videoUrl, p.captionsUrl].filter(Boolean)));
  for (const request of await cache.keys()) {
    const pathname = new URL(request.url).pathname, media = preparedMedia(pathname);
    if (media && kinds.includes(media[2]) && (!passageId || media[1] === passageId) && !current.has(pathname))
      await cache.delete(request);
  }
}
async function downloadStatus() {
  try {
    const cache = await caches.open("dm-media-v1");
    const ready = await cache.match("/offline-pack-ready");
    let complete = !!ready && (await ready.text()) === packVersion();
    if (complete)
      for (const p of passages)
        if (p.imageUrl && !(await cache.match(p.imageUrl))) {
          complete = false;
          break;
        }
    packStatus = complete
      ? "Text and images ready for offline study"
      : "Not downloaded";
    if (passage)
      videoStatus =
        (await cache.match(passage.videoUrl))?.status === 200 &&
        (!passage.captionsUrl || (await cache.match(passage.captionsUrl)))
          ? "Video and captions ready offline"
          : "This video is not downloaded";
    const estimate = await navigator.storage?.estimate();
    storageStatus = estimate
      ? `${Math.round((estimate.usage || 0) / 1024 / 1024)} MiB used on this device.`
      : "";
  } catch {
    packStatus =
      "Offline storage unavailable. Use an ordinary browser window with storage enabled.";
  }
}
async function downloadPack() {
  if (downloading) return;
  downloading = true;
  packStatus = "Downloading scripture and images…";
  renderBackground();
  try {
    if (!navigator.serviceWorker)
      throw new Error("Offline study requires HTTPS or localhost");
    await navigator.serviceWorker.ready;
    const cache = await caches.open("dm-media-v1");
    await cache.delete("/offline-pack-ready");
    const curriculum = await fetch("/api/curriculum", { cache: "reload" });
    if (!curriculum.ok) throw new Error("Curriculum unavailable");
    // Download from one curriculum snapshot and publish it only once its pictures are saved.
    const snapshot = (await curriculum.clone().json()).passages;
    const shellName = (await caches.keys()).find((n) =>
      n.startsWith("dm-shell-"),
    );
    if (!shellName) throw new Error("App shell is not ready");
    let count = 0;
    for (const p of snapshot) {
      if (p.imageUrl) await cacheFile(cache, p.imageUrl);
      count++;
      packStatus = `Downloading images: ${count} of ${snapshot.length}`;
      renderBackground();
    }
    await (await caches.open(shellName)).put("/api/curriculum", curriculum);
    await collectSupersededMedia(cache, snapshot, ["scene"]);
    await cache.put("/offline-pack-ready", new Response(packVersion(snapshot)));
    await downloadStatus();
    if (packVersion(snapshot) !== packVersion())
      packStatus = "Updated pictures downloaded. Reload the app to use them.";
  } catch (e) {
    packStatus = `Download failed: ${e.message}. Try again online; the pack is not ready.`;
  } finally {
    downloading = false;
    renderBackground();
  }
}
async function downloadVideo(id) {
  if (downloading) return;
  const p = passages.find((p) => p.id === id);
  downloading = true;
  videoStatus = "Downloading video and captions…";
  renderBackground();
  try {
    const cache = await caches.open("dm-media-v1");
    await cacheFile(cache, p.videoUrl);
    if (p.captionsUrl) await cacheFile(cache, p.captionsUrl);
    await collectSupersededMedia(cache, passages, ["video", "captions"], p.id);
    videoStatus = "Video and captions ready offline";
  } catch (e) {
    videoStatus = `Download failed: ${e.message}. Video is not ready.`;
  } finally {
    downloading = false;
    renderBackground();
  }
}
root.addEventListener("keydown", (e) => {
  if (e.key === "Escape" && trailSheet) document.querySelector('[data-action="trail-sheet-close"]')?.click();
  // Tab wraps inside an open dialog instead of leaving the page.
  const dialog = e.key === "Tab" && root.querySelector('[aria-modal="true"]');
  if (!dialog) return;
  const stops = [...dialog.querySelectorAll('button, a[href], input, summary, video[controls], [tabindex]:not([tabindex="-1"])')].filter(el => !el.disabled && el.getClientRects().length);
  if (!stops.length) return;
  const first = stops[0], last = stops.at(-1), inside = dialog.contains(document.activeElement);
  if (!inside || (e.shiftKey ? document.activeElement === first : document.activeElement === last)) {
    e.preventDefault();
    (e.shiftKey ? last : first).focus();
  }
});
// While a dialog is open (a trail stop or a landmark moment), everything else is inert, so focus stays inside it.
function containModal() {
  for (const el of root.querySelectorAll("[data-modal-inert]")) { el.inert = false; el.removeAttribute("data-modal-inert"); }
  let node = root.querySelector('[aria-modal="true"]');
  while (node && node !== root) {
    for (const sibling of node.parentElement.children)
      if (sibling !== node && !sibling.inert) { sibling.inert = true; sibling.dataset.modalInert = ""; }
    node = node.parentElement;
  }
}
// Light up the pace that matches the new-cards number, or Custom when none does.
function showPace(form) {
  const key = form.elements.newPerDay.value;
  let matched = false;
  for (const b of form.querySelectorAll('[data-action^="pace:"]')) {
    const on = b.dataset.action === `pace:${key}`;
    b.setAttribute("aria-pressed", String(on));
    matched ||= on;
  }
  const custom = form.querySelector('[data-live="pace-custom"]');
  if (custom) custom.hidden = matched;
}
root.addEventListener("input", (e) => {
  if (e.target.closest?.("#settings-form")) showPace(e.target.form);
  if (e.target.id === "passage-search") {
    search = e.target.value;
    writeLocation(true);
    renderBackground();
  }
});
root.addEventListener("click", async (e) => {
  const b = e.target.closest("[data-action]");
  if (!b) return;
  const [action, ...args] = b.dataset.action.split(":");
  const arg = args.join(":");
  try {
    if (action === "reload-app") {
      if (hasUnfinishedWork() && !confirm("Reload the app now? Your unrated answer and unsaved form edits will not be kept. Saved progress and downloads are kept.")) return;
      location.reload();
      return;
    }
    if (action === "nav") return await navigate(arg);
    if (action === "celebration-close") {
      celebration = null;
      render();
      focusStudy('[data-action="reveal"]');
      return;
    }
    // A stop on the trail map opens a sheet over the map; closing it returns focus to that stop.
    if (action === "trail-sheet") {
      trailSheet = arg;
      render();
      document.querySelector("#stop-sheet-title")?.focus({ preventScroll: true });
      return;
    }
    if (action === "trail-sheet-close") {
      const from = trailSheet;
      trailSheet = null;
      render();
      document.querySelector(`[data-action="trail-sheet:${CSS.escape(from || "")}"]`)?.focus({ preventScroll: true });
      return;
    }
    if (action === "welcome-close") {
      welcome = false;
      render();
      focusMain();
      return;
    }
    if (action === "toggle-streak") {
      await saveChange(current => ({...current,hideStreak:!current.hideStreak}));
      render();
      return;
    }
    if (action === "challenge-open") {
      await saveChange(current => ({...current,activeChallengeId:arg}));
      beginChallenge(arg);
      return await navigate("challenge");
    }
    if (action === "challenge-reveal" && challengeRound?.task) {
      challengeRound.revealed = true;
      render();
      root.querySelector(".answer-panel,.choice-list")?.focus();
      return;
    }
    if (action === "challenge-choice" && challengeRound?.task?.skill === "application" && challengeRound.revealed) {
      challengeRound.choice = arg;
      render();
      root.querySelector(".answer-panel")?.focus();
      return;
    }
    if (action === "challenge-location" && challengeRound?.task?.skill === "location" && challengeRound.revealed)
      return await recordChallengeAttempt({rating:Number(arg),hinted:false});
    if (action === "challenge-ack" && challengeRound?.task?.skill === "application" && challengeRound.choice)
      return await recordChallengeAttempt({choicePassageId:challengeRound.choice,knewWhy:arg === "yes",promptVariant:challengeRound.promptVariant || "initial"});
    if (action === "challenge-next" && challengeRound?.feedback) {
      chooseChallengeTask();
      render();
      focusStudy('[data-action="challenge-reveal"]');
      return;
    }
    if (action === "challenge-close") {
      if (!navigator.onLine) throw new Error("Teacher administration requires an online connection.");
      await api("/api/teacher/challenges/close", {challengeId:arg});
      await teacherRefresh();
      notice = "Challenge closed.";
      render();
      return;
    }
    if (action === "coverage") {
      if (!navigator.onLine) throw new Error("Teacher administration requires an online connection.");
      const value = b.closest("form")?.querySelector('[name="coveredAt"]')?.value;
      if (!value) throw new Error("Choose a coverage date first.");
      await api("/api/teacher/coverage", {passageId:arg,coveredAt:value});
      await teacherRefresh();
      notice = "Coverage date saved.";
      render();
      return;
    }
    if (action === "list") {
      search = listDestination.search;
      return await navigate(listDestination.route, {keepSearch:true});
    }
    if (action === "sync") {
      await syncer.sync();
      if (route === "teacher") {
        await teacherRefresh();
        renderBackground();
      }
      return;
    }
    if (action === "continue-link") {
      if (!navigator.onLine) throw new Error("Connect to the internet to sign in.");
      await openStudyPack();
      const token = linkToken;
      linkToken = null;
      if (!(await syncer.signIn("/api/login/link", {token}, confirmSwitch))) return keptOtherAccount();
      await signedInHere();
      return;
    }
    if (action === "login-mode") {
      loginMode = arg;
      loginMessage = "";
      if (arg !== "code") codeSent = false;
      render();
      root.querySelector({password:'#login-form [name="username"]', email:'#email-form [name="email"]', code:'#code-form [name="email"]'}[arg])?.focus({preventScroll:true});
      return;
    }
    if (action === "cancel-link") {
      linkToken = null;
      loginMessage = "";
      loginOpen = !local;
      render();
      return;
    }
    if (action === "reconnect") {
      loginOpen = true;
      loginMessage =
        "Sign in again to sync. Use the same account to keep your saved work.";
      render();
      return;
    }
    if (action === "signout") {
      const signedOut = await syncer.signout(() =>
        confirm(
          "You have unsynced work on this device. Signing out removes it permanently from this device. Cancel to keep it, or OK to sign out anyway.",
        ),
      );
      if (!signedOut) return;
      loginOpen = false;
      students = [];
      session = null;
      route = "today";
      return;
    }
    if (action === "start") {
      startSession();
      route = "review";
      writeLocation();
      render();
      focusStudy(session.intro ? '[data-action="intro-ready"]' : "");
      return;
    }
    if (action === "trail-stop") {
      const plan = model().trailPlan[arg];
      if (!plan?.cards.length) return;
      session = {done:0, target:plan.cards.length, practice:0, met:[], intro:null, queue:[...plan.cards], card:null, revealed:false, hinted:false, choice:null};
      nextCard();
      route = "review";
      writeLocation();
      render();
      focusStudy(session.intro ? '[data-action="intro-ready"]' : "");
      return;
    }
    if (action === "start-trail") {
      const m = model();
      if (m.queue.cards.length || m.queue.totalDue || m.journey.todayComplete) return;
      const candidates = passages.filter(p => m.local.snapshot.collectionIds.includes(p.id));
      if (!candidates.length) return;
      trailPassage = candidates[m.journey.practiceDays % candidates.length];
      trailRevealed = false;
      route = "trail";
      render();
      focusMain();
      return;
    }
    if (action === "reveal-trail" && route === "trail" && trailPassage) {
      trailRevealed = true;
      render();
      document.querySelector(".answer-panel")?.focus();
      return;
    }
    if (action === "finish-trail" && route === "trail" && trailPassage && trailRevealed) {
      const today = new Intl.DateTimeFormat("en-CA", {timeZone:"Europe/London", year:"numeric", month:"2-digit", day:"2-digit"}).format(new Date());
      const now = new Date().toISOString();
      const trail = {id:crypto.randomUUID(), cardId:`trail:${trailPassage.id}:${today}:v1`, passageId:trailPassage.id, at:now, mode:"trail", result:"done"};
      const daily = {id:crypto.randomUUID(), cardId:`daily:${today}:v1`, at:now, baseEventId:null, mode:"daily", result:"done"};
      await saveChange(current => ({...current, pending:[...current.pending, trail, daily]}));
      trailPassage = null;
      trailRevealed = false;
      return await navigate("today");
    }
    if (action === "intro-ready" && session?.intro) {
      session.met.push(session.intro);
      session.intro = null;
      render();
      focusStudy();
      return;
    }
    if (action === "finish") {
      await completeDay();
      return await navigate("today");
    }
    if (action === "apply-options" && session?.card?.kind === "apply") {
      session.revealed = true;
      render();
      return;
    }
    if (action === "apply-choice" && session?.card?.kind === "apply" && session.revealed && !session.choice) {
      session.choice = arg;
      render();
      document.querySelector(".answer-panel")?.focus();
      return;
    }
    if (action === "apply-ack" && session?.card?.kind === "apply" && session.choice) {
      const choice = model().applicationChoices.find(item => item.passageId === session.card.passageId);
      const covered = choice?.defensibleIds.includes(session.choice) && arg === "yes";
      return await rate({result: covered ? "covered" : "needs-practice", choicePassageId:session.choice, knewWhy:arg === "yes"});
    }
    if (action === "reveal" || action === "hint") {
      session.revealed = true;
      session.hinted = action === "hint";
      render();
      document.querySelector(".answer-panel")?.focus();
      return;
    }
    if (action === "rate")
      return await rate({ rating: Number(arg), hinted: session.hinted });
    if (action === "result") return await rate({ result: arg });
    if (action === "passage") {
      if (["browse","collection"].includes(route)) listDestination = {route, search};
      passage = passages.find((p) => p.id === arg);
      return await navigate("passage");
    }
    if (action === "add") {
      await saveChange((c) => ({
        ...c,
        additions: [...new Set([...c.additions, arg])],
      }));
      notice =
        "Added to your scriptures. Your daily new-card allowance is unchanged.";
      renderBackground();
      return;
    }
    if (action === "locate" || action === "help") {
      if (!model().local.snapshot.collectionIds.includes(arg))
        await saveChange((c) => ({
          ...c,
          additions: [...new Set([...c.additions, arg])],
        }));
      const card = cards.find((c) => c.id === `${arg}:${action}:v1`);
      await record(card, { result: action === "locate" ? "done" : "help" });
      notice =
        action === "locate"
          ? "Locating practice recorded."
          : "Help requested. Your teacher can see this after syncing.";
      renderBackground();
      return;
    }
    if (action === "pack") return await downloadPack();
    if (action === "pace") {
      const form = root.querySelector("#settings-form");
      form.elements.newPerDay.value = arg;
      showPace(form);
      return;
    }
    if (action === "video") return await downloadVideo(arg);
    // Play swaps the picture for the video without re-rendering, so playback starts from this tap.
    if (action === "play-video") {
      videoShown = arg;
      const block = root.querySelector(".media-block"), video = block?.querySelector("video");
      if (!video) return;
      // If the video can't load (offline and not downloaded), bring the picture back and say why.
      let failed = false;
      const unavailable = () => {
        if (failed || videoShown !== arg) return;
        failed = true;
        videoShown = null;
        notice = navigator.onLine ? "The video didn't load. Try again in a moment." : "This video isn't downloaded. Connect to the internet, or download it first.";
        render();
        root.querySelector(`[data-action="play-video:${CSS.escape(arg)}"]`)?.focus({ preventScroll: true });
      };
      video.querySelector("source")?.addEventListener("error", unavailable, { once: true });
      block.classList.add("playing");
      video.hidden = false;
      video.focus({ preventScroll: true });
      await video.play().catch(error => { if (error?.name !== "AbortError") unavailable(); });
      return;
    }
    if (action === "remove-video") {
      const cache = await caches.open("dm-media-v1");
      await cache.delete(passage.videoUrl);
      if (passage.captionsUrl) await cache.delete(passage.captionsUrl);
      await downloadStatus();
      renderBackground();
      return;
    }
    if (action === "remove-pack") {
      await caches.delete("dm-media-v1");
      await downloadStatus();
      renderBackground();
      return;
    }
    if (action === "resolve") {
      if (!navigator.onLine)
        throw new Error(
          "Teacher administration requires an online connection.",
        );
      await api("/api/teacher/help", { userId: args[0], eventId: args[1] });
      await teacherRefresh();
      renderBackground();
      return;
    }
  } catch (error) {
    if (action === "continue-link") { loginMessage = error.message; loginOpen = true; render(); return; }
    notice = error.message;
    renderBackground();
  }
});
root.addEventListener("submit", async (e) => {
  e.preventDefault();
  const form = e.target,
    data = new FormData(form);
  try {
    if (form.id === "login-form") {
      if (!navigator.onLine)
        throw new Error("Connect to the internet to sign in.");
      const username = data.get("username").trim();
      loginUsername = username;
      await openStudyPack();
      if (
        local &&
        username.toLowerCase() !== local.snapshot.user.username.toLowerCase()
      ) {
        if (
          !(await syncer.signout(() =>
            confirm(
              "Switching accounts removes unsynced work from this device. Cancel to keep it, or OK to continue.",
            ),
          ))
        )
          return;
      }
      const signedIn = await syncer.login(username, data.get("password"));
      if (!signedIn) return;
      await signedInHere();
      return;
    }
    if (form.id === "email-form") {
      if (!navigator.onLine) throw new Error("Connect to the internet to sign in.");
      signInEmail = data.get("email").trim();
      rememberSignInEmail();
      await api("/api/login/email", {email:signInEmail});
      codeSent = true;
      loginMode = "code";
      loginMessage = "";
      render();
      root.querySelector('#code-form [name="code"]')?.focus({preventScroll:true});
      return;
    }
    if (form.id === "code-form") {
      if (!navigator.onLine) throw new Error("Connect to the internet to sign in.");
      signInEmail = data.get("email").trim();
      rememberSignInEmail();
      await openStudyPack();
      if (!(await syncer.signIn("/api/login/code", {email:signInEmail, code:data.get("code")}, confirmSwitch))) return keptOtherAccount();
      await signedInHere();
      return;
    }
    if (form.id === "account-email-form") {
      if (!navigator.onLine) throw new Error("Connect to the internet to add your email.");
      const {email} = await api("/api/account/email", {email:data.get("email").trim()});
      accountEmailPending = email;
      notice = "";
      render();
      root.querySelector('#account-code-form [name="code"]')?.focus({preventScroll:true});
      return;
    }
    if (form.id === "account-code-form") {
      if (!navigator.onLine) throw new Error("Connect to the internet to add your email.");
      const {user} = await api("/api/account/email/confirm", {code:data.get("code")});
      accountEmailPending = "";
      // A sync already in flight may predate the change, so sync again until the account shows it.
      await syncer.sync();
      if (local?.snapshot.user.email !== user.email) await syncer.sync();
      notice = `Email added. Next time you can sign in with a code sent to ${user.email}.`;
      render();
      return;
    }
    if (form.id === "settings-form") {
      const invalid = [...form.querySelectorAll("input")].find(input => !input.validity.valid);
      const error = form.querySelector("#settings-error");
      if (invalid) {
        error.textContent = "Enter a whole number of new cards, from 0 to 20.";
        invalid.setAttribute("aria-invalid", "true");
        invalid.setAttribute("aria-describedby", "settings-error");
        invalid.focus({preventScroll:true});
        return;
      }
      error.textContent = "";
      for (const input of form.querySelectorAll("input")) input.removeAttribute("aria-invalid");
      const settings = { ...model().settings, newPerDay: Number(data.get("newPerDay")) };
      await saveChange((c) => ({ ...c, settings }));
      notice = "Settings saved on this device.";
      for (const input of form.querySelectorAll("input")) input.defaultValue = String(settings[input.name]);
      renderBackground();
      return;
    }
    if (form.id === "challenge-form") {
      const passageIds = data.getAll("passageIds");
      const skills = data.getAll("skills");
      if (!passageIds.length || !skills.length) throw new Error("Choose passages and at least one skill.");
      const goal = data.get("goal");
      await api("/api/teacher/challenges", {corpus:data.get("corpus"),passageIds,skills,goal,...(goal === "shared" ? {target:Number(data.get("target"))} : {})});
      await teacherRefresh();
      notice = "Class challenge opened.";
      render();
      return;
    }
    if (form.classList.contains("challenge-participants-form")) {
      await api("/api/teacher/challenges/participants", {challengeId:form.dataset.challenge,participantIds:data.getAll("participantIds")});
      await teacherRefresh();
      notice = "Challenge participants updated; the target is unchanged.";
      render();
      return;
    }
    if (!navigator.onLine)
      throw new Error("Teacher administration requires an online connection.");
    if (form.id === "student-form") {
      await api("/api/teacher/students", {
        username: data.get("username"),
        password: data.get("password"),
        email: data.get("email"),
      });
      notice = "Student account created. Share their credentials privately.";
    }
    if (form.id === "release-form") {
      await api("/api/teacher/release", {
        passageIds: data.getAll("passageIds"),
      });
      await syncer.sync();
      notice = "Passages released. Existing practice history is preserved.";
    }
    if (form.classList.contains("reset-form")) {
      await api("/api/teacher/reset-password", {
        userId: form.dataset.user,
        password: data.get("password"),
      });
      notice = "Password reset. The student must sign in again.";
    }
    if (form.classList.contains("email-form")) {
      const {user} = await api("/api/teacher/email", {
        userId: form.dataset.user,
        email: data.get("email"),
      });
      notice = user.email ? `Sign-in email saved for ${user.username}.` : `Sign-in email removed for ${user.username}.`;
    }
    await teacherRefresh();
    render();
  } catch (error) {
    if (["login-form", "email-form", "code-form"].includes(form.id)) {
      loginMessage = error.message;
      loginOpen = true;
      render();
      root.querySelector({"login-form":'[name="password"]', "email-form":'#email-form [name="email"]', "code-form":'#code-form [name="code"]'}[form.id])?.focus({preventScroll:true});
      root.querySelector("#main .feedback")?.scrollIntoView({ block: "nearest", behavior: "instant" });
    } else { notice = error.message; renderBackground(); }
  }
});
root.addEventListener("change", (e) => {
  if (e.target.name !== "corpus" || !e.target.closest("#challenge-form")) return;
  const days = e.target.value === "last30" ? 30 : e.target.value === "last60" ? 60 : null;
  const now = Date.now();
  for (const label of root.querySelectorAll(".challenge-passage-list label")) {
    const date = new Date(label.dataset.covered);
    const eligible = !days || (Number.isFinite(date.getTime()) && now - date.getTime() <= days * 86400000 && now >= date.getTime());
    label.hidden = !eligible;
    label.querySelector("input").checked = eligible;
  }
});
window.addEventListener("offline", () => {
  status = "Offline · changes saved on this device";
  renderBackground();
});
window.addEventListener("online", async () => {
  if (!local) {
    await syncer.restore();
    return;
  }
  await syncer.sync();
  if (route === "teacher") {
    await teacherRefresh();
    renderBackground();
  }
});
async function loadCurriculum() {
  const response = await fetch("/api/curriculum");
  if (!response.ok) throw new Error("Curriculum unavailable");
  const curriculum = await response.json();
  if (curriculum.schedulerVersion !== SCHEDULER_VERSION)
    throw new Error(
      "This app needs an update before studying. Reconnect and reload.",
    );
  passages = curriculum.passages;
  emailSignIn = curriculum.signIn?.email === true;
  cards = curriculum.cards;
}
// The page may have opened while the study pack was unreachable; load it before showing any study screen.
async function openStudyPack() {
  if (!passages.length)
    await loadCurriculum().catch((e) => {
      throw new Error(e.message.includes("update") ? e.message : "Unable to open the study pack. Check your connection and try again.");
    });
}
// Declining an account switch keeps the other account's unsynced work; say so on the sign-in page.
function keptOtherAccount() {
  if (status.startsWith("Your unsynced work is kept")) loginMessage = status;
  render();
}
const confirmSwitch = () => confirm("This sign-in opens a different account. Switching removes the other account's unsynced work from this device. Cancel to keep it, or OK to continue.");
// Kept for this tab only, so a home-screen app that reloads while the student reads the email
// still has the address filled in next to the code box.
function rememberSignInEmail() {
  try { sessionStorage.setItem("dm-signin-email", signInEmail); } catch {}
}
async function signedInHere() {
  loginOpen = false;
  loginMessage = "";
  codeSent = false;
  loginMode = "password";
  linkToken = null;
  route = "today";
  writeLocation(true);
  loginUsername = "";
  students = [];
  await greet(true);
  render();
  focusMain();
  void syncer.sync();
}
// The emailed link carries its token in the address's fragment, which never reaches the server.
// Take it out of the address bar and history at once; nothing is spent until the student continues.
function takeLinkToken() {
  const match = /^#verify\?t=([A-Za-z0-9_-]{20,128})$/.exec(location.hash);
  if (!match && !location.hash.startsWith("#verify")) return;
  history.replaceState(null, "", location.pathname + location.search);
  if (match) { linkToken = match[1]; loginOpen = true; }
}
async function boot() {
  takeLinkToken();
  try { signInEmail = sessionStorage.getItem("dm-signin-email") || ""; } catch {}
  // Back from reading the email: open at the code box, with the address still filled in.
  if (signInEmail) loginMode = "code";
  try {
    if ("serviceWorker" in navigator)
      await navigator.serviceWorker.register("/sw.js");
    await loadCurriculum();
    await syncer.restore();
    if (local) { readLocation(); await navigate(route, {fromHistory:true, keepSearch:true}); await greet(false); if (welcome) { render(); focusMain(); } }
    else render();
    if (local) void syncer.sync();
  } catch (e) {
    loginMessage = `Unable to open the study pack: ${e.message}. Connect online and reload.`;
    render();
  }
}
void boot();
